/**
 * Tracker writes (create, update, log an entry) through the global apiClient. Non-throwing;
 * callers map failures to their own errors.
 */

import type { LogClientTrackerEntryBody } from '@schemas/trackers';
import { api, type ApiResponse } from '@/lib/apiClient';

type Row = Record<string, any>;

export function createTracker(body: {
  clientId: string;
  title: string;
  unit: string;
  targetDirection: string;
  schedule: unknown;
}): Promise<ApiResponse & { tracker?: Row }> {
  return api.post('/api/coach/trackers', body);
}

/**
 * PUT /api/coach/trackers/:id requires `If-Match: "<updated_at>"` (optimistic concurrency: 428
 * without it, 409 if the tracker changed since). The client-side cache can't be trusted for that
 * value (e.g. logging an entry stamps a local `updatedAt`), so read the current version first,
 * then write with it. Concurrent edits between the two calls still get a 409.
 *
 * Regression: this update used to send no If-Match at all, so every tracker edit 428'd.
 */
export async function updateTracker(
  trackerId: string,
  patch: {
    title?: string;
    unit?: string;
    targetDirection?: string;
    schedule?: unknown;
    showInAnalytics?: boolean;
  },
): Promise<ApiResponse & { tracker?: Row }> {
  const path = `/api/coach/trackers/${encodeURIComponent(trackerId)}`;
  const current = await api.get<unknown>(path, { skipRetry: true });
  const updatedAt = (current as { tracker?: { updatedAt?: string } }).tracker?.updatedAt;
  if (!current.ok || !updatedAt) return current;

  return api.put(path, patch, { headers: { 'If-Match': `"${updatedAt}"` } });
}

export function addTrackerEntry(body: {
  trackerId: string;
  clientId: string;
  value: number;
  note?: string;
  createdBy: 'client' | 'coach';
}): Promise<ApiResponse & { entry?: Row }> {
  return api.post('/api/coach/tracker-entries', body);
}

/**
 * A client logging an entry on their own tracker. Tracker values are encrypted at rest and only
 * the server holds the key, so this must go through the endpoint; a direct browser insert of the
 * plaintext value is rejected by RLS. Coaches use addTrackerEntry above.
 */
export function logClientTrackerEntry(body: LogClientTrackerEntryBody): Promise<ApiResponse & { entry?: Row }> {
  return api.post('/api/client/tracker-entries', body);
}
